import {Routes, Route, RouterProvider, createBrowserRouter, createRoutesFromElements, useLocation, Navigate, Outlet, useParams} from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { UserContext, UserProvider } from "./services/UserContext"
import { useCommune, CommuneProvider } from "./services/CommuneContext"
import Login from './scenes/Login';
import Accueil from './scenes/Accueil';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import {Box, CircularProgress} from '@mui/material';
import { useAPI } from './services/API';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/fr';
import {SnackbarProvider} from 'notistack';
import Plan from './scenes/Plan';
import Concessions from './scenes/Concessions';
import Creation_Concession from './scenes/Creation_Concession';
import Concession from './scenes/Concession';
import Recherche from './scenes/Recherche';
import Alertes from './scenes/Alertes';
import Parametres from './scenes/Parametres';
import DefinirMotDePasse from './scenes/DefinirMotDePasse';
import Tempo from './scenes/Tempo';
import CRM from './scenes/CRM';

const theme = createTheme({
    palette: {
        primary: {
            main: "#27264A",
            light : "#5957a8",
            lighter : "#d0cfe6"
        },
        secondary:{
            main: "#e10716"
        },
        disabled:{
            main: "#E9E9EC"
        },
        background: {
            main: "#F6F6F9",
        },
        gris:{
            contour:"#CCCCD1"
        },
        emeraude:{
            main: "#3DE192",
            light:"#DCF9EB",
        },
        orchide:{
            main: "#DE7CD0",
            light:"#F7DEF3"
        }
    },
    typography: {
        fontFamily: "Poppins"
    }
});



export default function App() {

    const router = createBrowserRouter(
        createRoutesFromElements(
            <>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="login" element={<Login />} />
                <Route path="invitation/:token" element={<DefinirMotDePasse />} />
                <Route path="crm" element={<RequireAuth><RequireCrmAccess><CRM /></RequireCrmAccess></RequireAuth>} />

                {/* Commune dynamique */}
                <Route path=":commune" element={<AppWithCommune />}>
                    <Route element={<RequireAuth><Accueil /></RequireAuth>} >
                        <Route index element={<Plan/>}/>
                        <Route path="plan" element={<Plan/>}/>
                        <Route path="concessions" element={<Concessions/>}/>
                        <Route path="concession/creer/" element={<Creation_Concession/>}/>
                        <Route path="concession/creer/:Emplacement_nom" element={<Creation_Concession/>}/>
                        <Route path="concession/:numero" element={<Concession/>}/>
                        <Route path="recherche/:input" element={<Recherche/>}/>
                        <Route path="alertes" element={<Alertes/>}/>
                        <Route path="parametres" element={<RequireAdminOrSuper><Parametres/></RequireAdminOrSuper>}/>
                        <Route path="tempo/:pageSuivante" element={<Tempo/>}/>
                    </Route>
                </Route>

            </>
        )
    );

    return (
        <ThemeProvider theme={theme}>
            <UserProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                    <SnackbarProvider maxSnack={5}>
                        <RouterProvider router={router} /> 
                    </SnackbarProvider>
                </LocalizationProvider>
            </UserProvider>
        </ThemeProvider>
    );
}


function AppWithCommune() {
  const { commune } = useParams();

  // Optionnel : tu peux valider ici que "commune" est connue
  // Ou stocker dans un Context global

  return (
    <CommuneProvider value={commune}>
      <Outlet />
    </CommuneProvider>
  );
} 


function RequireAuth({ children }) {
    const [userContext, setUserContext] = useContext(UserContext)
    let location = useLocation();
    const isLocalhost = false //window.location.href.includes("localhost")
    const refreshToken = useRefreshToken(setUserContext);

    useEffect(() => {
        if (!isLocalhost) {
            console.log("Démarrage intervalle de refreshToken");

            // Appel immédiat
            refreshToken()
                .then(token => console.log("Token rafraîchi", token))
                .catch(err => console.warn("Erreur refresh:", err));

            // Puis toutes les 5 minutes
            const interval = setInterval(() => {
                console.log("Appel refresh token (interval)");
                refreshToken()
                    .then(token => console.log("Token rafraîchi", token))
                    .catch(err => console.warn("Erreur refresh:", err));
            }, 5 * 60 * 1000);

            return () => clearInterval(interval); // Nettoyage à l'unmount
        }
    }, []);


    /** Sync logout across tabs **/
    const syncLogout = useCallback(event => {
        if (event.key === "logout") {
            // If using react-router-dom, you may call history.push("/")
            window.location.reload()
        }
    }, [])
    useEffect(() => {
        window.addEventListener("storage", syncLogout)
        return () => {
            window.removeEventListener("storage", syncLogout)
        }
    }, [syncLogout])




  
    if(userContext.token){
        return children;
    }else if (userContext.token===null || isLocalhost) {
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
      return <Navigate to="/login" state={{ from: location }} replace />;
    }else{
        return(
            <Box sx={{ display: 'flex' }}>
                <CircularProgress sx={{display:"block", mx:"auto", my:4}} />
            </Box>
        )
    }
}



function RequireAdminOrSuper({ children }) {
    const [userContext] = useContext(UserContext);
    if (!['super', 'admin'].includes(userContext.role)) {
        return <Navigate to=".." replace />;
    }
    return children;
}


const CRM_ROLES = ['super', 'collaborator'];

function RequireCrmAccess({ children }) {
    const [userContext] = useContext(UserContext);
    if (!CRM_ROLES.includes(userContext.role)) {
        return <Navigate to="/login" replace />;
    }
    return children;
}


function useRefreshToken(setUserContext) {
    const api = useAPI();
    const refreshTokenPromiseRef = useRef(null);

    const refreshToken = useCallback(() => {
        console.log("Coucou1")
        if (!refreshTokenPromiseRef.current) {
            console.log("Coucou2")
            refreshTokenPromiseRef.current = api("POST", "utilisateur/refreshToken", {}, {}, "application/json", { credentials: "include" })
                .then(res => {
                    if (res.ok) {
                        setUserContext(currVal => ({ ...currVal, ...res.json }));
                        return res.json.token;
                    } else {
                        setUserContext(currVal => ({ ...currVal, token: null }));
                        throw new Error("Refresh failed");
                    }
                })
                .finally(() => {
                    refreshTokenPromiseRef.current = null;
                });
        }

        return refreshTokenPromiseRef.current;
    }, [api, setUserContext]);

    return refreshToken;
}